/**
 * Editor de **tablero disponible** (réplica de `renderStockTable` del SSR).
 *
 * Presets de medida (estándar / mediano / personalizado), dimensiones personalizadas
 * cuando se elige "custom", y el recomendador de lámina por área (qué preset gasta
 * menos láminas). Presentacional: recibe el tablero y las piezas; emite cambios al
 * orquestador. No toca la API ni mantiene estado propio salvo el preset forzado, que
 * es decisión de UI local del padre.
 */
import {
  PRESETS,
  type StockPreset,
} from "../constants";
import { calcRecommendation, rectM2 } from "../format";
import type { Part, Stock } from "../types";

interface StockEditorProps {
  stock: Stock;
  parts: Part[];
  /** Preset elegido a mano (sobre el detectado por dimensiones); `null` = auto. */
  forcedPresetId: StockPreset["id"] | null;
  onSelectPreset: (preset: StockPreset) => void;
  onUpdateStock: (patch: Partial<Stock>) => void;
}

export function StockEditor({
  stock,
  parts,
  forcedPresetId,
  onSelectPreset,
  onUpdateStock,
}: StockEditorProps) {
  const detected =
    PRESETS.find((p) => p.w === stock.width && p.h === stock.height)?.id ?? "custom";
  const activePreset = forcedPresetId ?? detected;
  const recs = calcRecommendation(parts);
  const best = recs && recs[0];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        {PRESETS.map((preset) => {
          const active = activePreset === preset.id;
          const isBest =
            best && best.id === preset.id && preset.w && parts.length > 0;
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={active}
              onClick={() => onSelectPreset(preset)}
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                active
                  ? "border-brand bg-brand/5"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-slate-800">
                    {preset.label}
                  </div>
                  {preset.desc ? (
                    <div className="text-xs text-slate-500">{preset.desc}</div>
                  ) : null}
                </div>
                {isBest ? (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">
                    Recomendado
                  </span>
                ) : null}
              </div>

              {preset.id === "custom" && active ? (
                <div className="mt-2 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Ancho (cm)
                    <input
                      type="number"
                      min={1}
                      step={1}
                      aria-label="Ancho del tablero"
                      className="h-9 rounded-md border border-slate-300 px-2 text-sm"
                      value={stock.width}
                      onChange={(e) =>
                        onUpdateStock({
                          width: Math.max(1, parseFloat(e.target.value) || 1),
                          label: "Personalizado",
                        })
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Largo (cm)
                    <input
                      type="number"
                      min={1}
                      step={1}
                      aria-label="Largo del tablero"
                      className="h-9 rounded-md border border-slate-300 px-2 text-sm"
                      value={stock.height}
                      onChange={(e) =>
                        onUpdateStock({
                          height: Math.max(1, parseFloat(e.target.value) || 1),
                          label: "Personalizado",
                        })
                      }
                    />
                  </label>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between px-1 text-xs text-slate-500">
        <span>
          Área:{" "}
          <strong className="text-slate-700">
            {rectM2(stock.width, stock.height, 4)} m²
          </strong>
        </span>
        <span>
          {stock.width} × {stock.height} cm
        </span>
      </div>

      {recs && parts.length > 0 ? (
        <div className="mt-1">
          <div className="mb-2 text-xs font-bold text-brand">
            Recomendador de lámina
          </div>
          <ul className="flex flex-col gap-1">
            {recs.map((r, i) => {
              const effColor =
                r.efficiency >= 80
                  ? "text-green-600"
                  : r.efficiency >= 60
                    ? "text-orange-600"
                    : "text-red-500";
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      const preset = PRESETS.find((p) => p.id === r.id);
                      if (preset) onSelectPreset(preset);
                    }}
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">
                        {i + 1}. {r.label}
                      </span>
                      {i === 0 ? (
                        <span className="text-[10px] font-bold text-green-700">
                          Mejor
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500">
                      <strong>{r.estimatedSheets}</strong> lámina
                      {r.estimatedSheets !== 1 ? "s" : ""} ·{" "}
                      <strong className={effColor}>~{r.efficiency}%</strong> aprovechado
                      {!r.allFit ? (
                        <span className="text-red-500">
                          {" "}
                          · {r.noFit} no cabe{r.noFit > 1 ? "n" : ""}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="mt-1 text-xs text-slate-400">
            Estimación por área total de piezas. Optimizar da el resultado exacto.
          </p>
        </div>
      ) : null}
    </div>
  );
}
